import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FaSearch } from "react-icons/fa";
import CandidateTemp from "./CandidateTemp";
import { API_BASE_URL } from "../../../config/api";

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const PAGE_SIZE = 10; // 10 per page

const Candidatelist = () => {
  const [searchValue, setSearchValue] = useState("");
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [page, setPage] = useState(1);

  // Fetch unfiltered admin jobs
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setErr("");

        const res = await axios.get(`${API_BASE_URL}/api/jobs/admin/all`, {
          headers: { ...authHeaders() },
          params: { _ts: Date.now() },
        });

        const list = Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data)
          ? res.data
          : [];

        if (!cancelled) {
          setJobs(list);
          setPage(1); // reset to first page
        }
      } catch (error) {
        if (!cancelled) {
          setJobs([]);
          setErr(error?.response?.data?.message || error.message || "Failed to load jobs");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // recompute total pages with search
  const totalPages = useMemo(() => {
    const q = (searchValue || "").toLowerCase().trim();
    const filteredCount = q
      ? jobs.filter((job) => {
          const jid   = (job.jobId || job.id || job._id || "").toString().toLowerCase();
          const title = (job.positionTitle || job.title || job.name || "").toLowerCase();
          const ref   = (job.referenceNumber || job.requestId || job.refNo || "").toLowerCase();
          const reg   = (job.region || job.city || job.location || "").toLowerCase();
          return jid.includes(q) || title.includes(q) || ref.includes(q) || reg.includes(q);
        }).length
      : jobs.length;

    return Math.max(1, Math.ceil(filteredCount / PAGE_SIZE));
  }, [jobs, searchValue]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      document
        .getElementById("admin-jobs-scroll-area")
        ?.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <div className="min-h-screen p-6 bg-gray-50">
      {err && (
        <div className="mb-4 p-3 rounded border border-red-200 bg-red-50 text-red-700 text-sm">
          {err}
        </div>
      )}

      {/* Search (top-left), then Total jobs directly UNDER it (aligned to search width) */}
      <div className="mb-3">
        <div className="relative w-full max-w-md">
          <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchValue}
            onChange={(e) => {
              setSearchValue(e.target.value);
              setPage(1);
            }}
            placeholder="Search by ID"
            className="w-full pl-10 pr-4 py-3 rounded-full border border-gray-200 bg-white text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-300"
          />
        </div>
        <div className="text-sm text-gray-700 mt-2 max-w-md">
          Total jobs: <span className="font-semibold">{jobs.length}</span>
        </div>
      </div>

      {/* Cards BELOW the search */}
      <CandidateTemp
        jobs={jobs}
        loading={loading}
        searchValue={searchValue}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default Candidatelist;
